import * as THREE from 'three/webgpu';
import {
	Fn,
	Loop,
	color,
	float,
	instanceIndex,
	max,
	min,
	mix,
	positionWorld,
	smoothstep,
	texture,
	textureStore,
	uniform,
	vec2,
	vec3,
	vec4
} from 'three/tsl';
import { createCloudDensitySampler } from './createCloudDensitySampler.js';

export const DEFAULT_CLOUD_SHADOW_PARAMETERS = Object.freeze( {
	enabled: true,
	resolution: 256,
	distance: 6000,
	samples: 8,
	strength: 0.62,
	softness: 2.5,
	density: 0.6,
	minimumLight: 0.38,
	gamma: 0.7
} );

const RANGES = Object.freeze( {
	resolution: [ 64, 1024 ],
	distance: [ 1000, 12000 ],
	samples: [ 2, 32 ],
	strength: [ 0, 1 ],
	softness: [ 0, 8 ],
	density: [ 0.05, 2 ],
	minimumLight: [ 0, 0.9 ],
	gamma: [ 0.2, 2 ]
} );

function clampParameters( parameters ) {

	const next = { ...parameters };
	for ( const [ key, range ] of Object.entries( RANGES ) ) {

		if ( Number.isFinite( next[ key ] ) ) next[ key ] = Math.min( range[ 1 ], Math.max( range[ 0 ], next[ key ] ) );

	}
	next.resolution = Math.max( 64, Math.round( next.resolution / 64 ) * 64 );
	next.samples = Math.round( next.samples );
	next.enabled = next.enabled !== false;
	delete next.updateRate;
	delete next.smoothing;
	return next;

}

function makeTexture( resolution, name ) {

	const target = new THREE.StorageTexture( resolution, resolution );
	target.name = name;
	target.type = THREE.HalfFloatType;
	target.format = THREE.RGBAFormat;
	target.colorSpace = THREE.NoColorSpace;
	target.minFilter = THREE.LinearFilter;
	target.magFilter = THREE.LinearFilter;
	target.wrapS = THREE.ClampToEdgeWrapping;
	target.wrapT = THREE.ClampToEdgeWrapping;
	return target;

}

export class WebGPUCloudShadowMap {

	constructor( renderer, clouds, options = {} ) {

		if ( ! renderer?.isWebGPURenderer ) throw new TypeError( 'WebGPUCloudShadowMap requires a Three.js WebGPURenderer.' );
		if ( ! clouds?.resources?.baseTexture ) throw new TypeError( 'WebGPUCloudShadowMap requires a WebGPUCloudVolume.' );

		this.renderer = renderer;
		this.clouds = clouds;
		this.parameters = clampParameters( { ...DEFAULT_CLOUD_SHADOW_PARAMETERS, ...options } );
		this.computeCenter = uniform( new THREE.Vector2() );
		this.cloudPosition = uniform( clouds.position.clone() );
		this.groundY = uniform( options.groundY ?? - 1 );
		this.distance = uniform( this.parameters.distance );
		this.samples = uniform( this.parameters.samples );
		this.softness = uniform( this.parameters.softness );
		this.shadowDensity = uniform( this.parameters.density );
		this.minimumLight = uniform( this.parameters.minimumLight );
		this.gamma = uniform( this.parameters.gamma );
		this.strength = uniform( this.parameters.strength );
		this.enabled = uniform( this.parameters.enabled ? 1 : 0 );
		this._initialized = false;
		this._createResources();

	}

	_createResources() {

		const resolution = this.parameters.resolution;
		this.rawTexture = makeTexture( resolution, 'CyborgClouds raw terrain shadow' );
		this.blurTexture = makeTexture( resolution, 'CyborgClouds horizontal terrain shadow blur' );
		this.outputTexture = makeTexture( resolution, 'CyborgClouds current terrain shadow' );
		this.outputTextureNode ??= texture( this.outputTexture );
		this.outputTextureNode.value = this.outputTexture;
		this._buildComputeNodes();

	}

	_buildComputeNodes() {

		const resolution = this.parameters.resolution;
		const cloudUniforms = this.clouds.cloudUniforms;
		const sampleDensity = createCloudDensitySampler(
			this.clouds.resources.baseTexture,
			this.clouds.resources.detailTexture,
			cloudUniforms
		);

		const generate = Fn( () => {

			const id = instanceIndex;
			const x = id.mod( resolution );
			const y = id.div( resolution );
			const uv = vec2( x, y ).add( 0.5 ).div( resolution );
			const groundXZ = this.computeCenter.add( uv.sub( 0.5 ).mul( this.distance ) );
			const sunDirection = cloudUniforms.sunDirection;
			const safeSunY = max( sunDirection.y, 0.08 );
			const opticalDepth = float( 0 ).toVar();

			Loop( this.samples, ( { i } ) => {

				const layerT = float( i ).add( 0.5 ).div( this.samples );
				const worldY = this.cloudPosition.y.sub( cloudUniforms.volumeSize.y.mul( 0.5 ) )
					.add( cloudUniforms.volumeSize.y.mul( layerT ) );
				const rayDistance = worldY.sub( this.groundY ).div( safeSunY );
				const sampleXZ = groundXZ.add( sunDirection.xz.mul( rayDistance ) );
				const localPosition = vec3(
					sampleXZ.x.sub( this.cloudPosition.x ).div( cloudUniforms.volumeSize.x ),
					layerT.sub( 0.5 ),
					sampleXZ.y.sub( this.cloudPosition.z ).div( cloudUniforms.volumeSize.z )
				);
				const interval = cloudUniforms.volumeSize.y.div( this.samples ).div( safeSunY );
				opticalDepth.addAssign( sampleDensity( localPosition, float( 1 ) ).x.mul( interval ) );

			} );

			const transmittance = opticalDepth.mul( cloudUniforms.extinction )
				.mul( this.shadowDensity ).mul( - 0.82 ).exp();
			textureStore( this.rawTexture, vec2( x, y ), vec4( transmittance, transmittance, transmittance, 1 ) );

		} );
		this.generateNode = generate().compute( resolution * resolution ).setName( 'generateCloudTerrainShadow' );

		const raw = texture( this.rawTexture, null, 0 );
		const blurHorizontal = Fn( () => {

			const id = instanceIndex;
			const x = id.mod( resolution );
			const y = id.div( resolution );
			const uv = vec2( x, y ).add( 0.5 ).div( resolution );
			const radius = this.softness.add( 0.5 ).div( resolution );
			const center = raw.sample( uv ).r;
			const blurred = raw.sample( uv.add( vec2( radius, 0 ) ) ).r
				.add( raw.sample( uv.sub( vec2( radius, 0 ) ) ).r )
				.add( center.mul( 2 ) ).mul( 0.25 );
			const value = mix( center, blurred, min( this.softness, 1 ) );
			textureStore( this.blurTexture, vec2( x, y ), vec4( value, value, value, 1 ) );

		} );
		this.horizontalBlurNode = blurHorizontal().compute( resolution * resolution ).setName( 'blurCloudTerrainShadowHorizontal' );

		const horizontal = texture( this.blurTexture, null, 0 );
		const verticalBlur = Fn( () => {

			const id = instanceIndex;
			const x = id.mod( resolution );
			const y = id.div( resolution );
			const uv = vec2( x, y ).add( 0.5 ).div( resolution );
			const radius = this.softness.add( 0.5 ).div( resolution );
			const center = horizontal.sample( uv ).r;
			const blurred = horizontal.sample( uv.add( vec2( 0, radius ) ) ).r
				.add( horizontal.sample( uv.sub( vec2( 0, radius ) ) ).r )
				.add( center.mul( 2 ) ).mul( 0.25 );
			const value = mix( center, blurred, min( this.softness, 1 ) );
			textureStore( this.outputTexture, vec2( x, y ), vec4( value, value, value, 1 ) );

		} );
		this.verticalBlurNode = verticalBlur().compute( resolution * resolution ).setName( 'blurCloudTerrainShadowVertical' );
		this.computeNodes = [ this.generateNode, this.horizontalBlurNode, this.verticalBlurNode ];

	}

	createTerrainShadowNode() {

		const currentUV = positionWorld.xz.sub( this.computeCenter ).div( this.distance ).add( 0.5 );
		const edge = min(
			min( currentUV.x, currentUV.y ),
			min( float( 1 ).sub( currentUV.x ), float( 1 ).sub( currentUV.y ) )
		);
		const edgeFade = smoothstep( 0, 0.025, edge );
		const transmittance = this.outputTextureNode.sample( currentUV ).r
			.pow( this.gamma ).max( this.minimumLight );
		const amount = this.strength.mul( this.enabled ).mul( edgeFade );
		return mix( 1, transmittance, amount );

	}

	createTerrainColorNode( baseColor = 0x244547 ) {

		return color( baseColor );

	}

	async initialize( camera ) {

		await this.renderer.init();
		this._prepareFrame( camera );
		this.renderer.compute( this.computeNodes );
		this._initialized = true;
		return this;

	}

	setParameters( patch = {} ) {

		const previousResolution = this.parameters.resolution;
		this.parameters = clampParameters( { ...this.parameters, ...patch } );
		this.distance.value = this.parameters.distance;
		this.samples.value = this.parameters.samples;
		this.softness.value = this.parameters.softness;
		this.shadowDensity.value = this.parameters.density;
		this.minimumLight.value = this.parameters.minimumLight;
		this.gamma.value = this.parameters.gamma;
		this.strength.value = this.parameters.strength;
		this.enabled.value = this.parameters.enabled ? 1 : 0;
		if ( previousResolution !== this.parameters.resolution ) {

			this._disposeResources();
			this._createResources();
			this._initialized = false;

		}
		return this;

	}

	update( camera ) {

		if ( ! this.parameters.enabled ) return;
		this._prepareFrame( camera );
		this.renderer.compute( this.computeNodes );

	}

	_prepareFrame( camera ) {

		const texelWorld = this.parameters.distance / this.parameters.resolution;
		this.computeCenter.value.set(
			Math.round( camera.position.x / texelWorld ) * texelWorld,
			Math.round( camera.position.z / texelWorld ) * texelWorld
		);
		this.cloudPosition.value.copy( this.clouds.position );

	}

	getStats() {

		return {
			...this.parameters,
			approximateTextureBytes: this.parameters.resolution ** 2 * 4 * 2 * 3
		};

	}

	_disposeResources() {

		this.generateNode?.dispose();
		this.horizontalBlurNode?.dispose();
		this.verticalBlurNode?.dispose();
		this.rawTexture?.dispose();
		this.blurTexture?.dispose();
		this.outputTexture?.dispose();

	}

	dispose() {

		this._disposeResources();

	}

}
